import { useQueries, useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  useLocation,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { WorkspaceViewFilters } from '@molt/shared';
import { Button, EmptyState, Skeleton } from '@/components';
import { SavedWorkspaceViewPage } from '@/components/workspace/SavedWorkspaceViewPage';
import {
  WorkspaceCalendarPage,
  type WorkspaceCalendarListOption,
} from '@/components/workspace/WorkspaceCalendarPage';
import {
  WorkspaceTablePage,
  type WorkspaceTableListOption,
} from '@/components/workspace/WorkspaceTablePage';
import { workspaceBoardsQueryOptions } from '@/lib/boards';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { boardLabelsQueryOptions } from '@/lib/labels';
import { boardListsQueryOptions } from '@/lib/lists';
import { weekStartForLocale } from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import { signInHref, useSession } from '@/lib/session';
import {
  calendarFilterFromWorkspaceView,
  tableFilterFromWorkspaceView,
  useDeleteWorkspaceView,
  useRenameWorkspaceView,
  useSetWorkspaceViewBoards,
  useSetWorkspaceViewFilters,
  useSetWorkspaceViewVisibility,
  workspaceSavedCalendarQueryOptions,
  workspaceSavedTableQueryOptions,
  workspaceViewFiltersFromCalendar,
  workspaceViewFiltersFromTable,
  workspaceViewQueryOptions,
} from '@/lib/workspace-views';
import {
  nextCalendarMonth,
  previousCalendarMonth,
  workspaceCalendarMonth,
} from '@/lib/workspace-calendar';
import { workspaceMembersQueryOptions, workspaceQueryOptions } from '@/lib/workspaces';

interface SavedWorkspaceViewSearch {
  readonly month?: string | undefined;
}

export const Route = createFileRoute('/workspace-views/$viewId')({
  staticData: { layout: 'full-bleed' },
  validateSearch: (search: Record<string, unknown>): SavedWorkspaceViewSearch => {
    const month = typeof search.month === 'string' ? search.month : undefined;
    return month !== undefined && /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? { month } : {};
  },
  component: SavedWorkspaceViewRoute,
});

function SavedWorkspaceViewRoute() {
  const { t } = useInterfaceLanguage();
  const { viewId } = Route.useParams();
  const search = Route.useSearch();
  const location = useLocation();
  const router = useRouter();
  const navigate = useNavigate();
  const {
    profile,
    isPending: sessionPending,
    isSignedOut,
    error: sessionError,
  } = useSession();
  const canFetch = !isSignedOut && !sessionPending && profile !== null;
  const view = useQuery({
    ...workspaceViewQueryOptions(viewId),
    enabled: canFetch,
  });
  const workspaceId = view.data?.workspace.id;
  const workspace = useQuery({
    ...workspaceQueryOptions(workspaceId ?? viewId),
    enabled: canFetch && workspaceId !== undefined,
  });
  const boards = useQuery({
    ...workspaceBoardsQueryOptions(workspaceId ?? viewId, 'open'),
    enabled: canFetch && workspaceId !== undefined,
  });
  const members = useQuery({
    ...workspaceMembersQueryOptions(workspaceId ?? viewId),
    enabled: canFetch && workspaceId !== undefined,
  });
  const { language } = useLocalePreferencesValue();
  const month = workspaceCalendarMonth(search.month, weekStartForLocale(language));
  const table = useQuery({
    ...workspaceSavedTableQueryOptions(viewId),
    enabled: canFetch && view.data?.kind === 'table',
  });
  const calendar = useQuery({
    ...workspaceSavedCalendarQueryOptions(viewId, {
      from: month.from,
      to: month.to,
    }),
    enabled: canFetch && view.data?.kind === 'calendar',
  });
  const selectedBoardIds = view.data?.selectedBoardIds ?? [];
  const selectedBoards = (boards.data ?? []).filter((board) =>
    selectedBoardIds.includes(board.id),
  );
  const labelQueries = useQueries({
    queries: selectedBoards.map((board) => ({
      ...boardLabelsQueryOptions(board.id),
      enabled: canFetch,
    })),
  });
  const listQueries = useQueries({
    queries: selectedBoards.map((board) => ({
      ...boardListsQueryOptions(board.id, 'open'),
      enabled: canFetch,
    })),
  });
  const labels = dedupeById(labelQueries.flatMap((query) => query.data ?? []));
  const tableLists = selectedBoards.flatMap<WorkspaceTableListOption>((board, index) =>
    (listQueries[index]?.data ?? []).map((list) => ({
      id: list.id,
      title: list.title,
      boardId: board.id,
      boardTitle: board.title,
    })),
  );
  const calendarLists = selectedBoards.flatMap<WorkspaceCalendarListOption>(
    (board, index) =>
      (listQueries[index]?.data ?? []).map((list) => ({
        ...list,
        boardTitle: board.title,
      })),
  );
  const loadingFilters =
    members.isPending ||
    labelQueries.some((query) => query.isPending) ||
    listQueries.some((query) => query.isPending);
  const renameView = useRenameWorkspaceView();
  const setVisibility = useSetWorkspaceViewVisibility();
  const setBoards = useSetWorkspaceViewBoards();
  const setFilters = useSetWorkspaceViewFilters();
  const deleteView = useDeleteWorkspaceView();
  const [localSelectedBoardIds, setLocalSelectedBoardIds] = useState<
    readonly string[] | null
  >(null);
  const [localFilters, setLocalFilters] = useState<WorkspaceViewFilters | null>(null);
  const initialisedViewId = useRef<string | null>(null);

  useEffect(() => {
    if (view.data === undefined) {
      setLocalSelectedBoardIds(null);
      setLocalFilters(null);
      initialisedViewId.current = null;
      return;
    }
    if (initialisedViewId.current === view.data.id) return;
    setLocalSelectedBoardIds(view.data.selectedBoardIds);
    setLocalFilters(view.data.filters);
    initialisedViewId.current = view.data.id;
  }, [view.data]);

  if (isSignedOut) {
    return (
      <SavedWorkspaceViewFrame title={t('workspaceViews.saved.title')}>
        <EmptyState
          tone="neutral"
          size="panel"
          headingLevel={2}
          title={t('workspaceViews.saved.signInTitle')}
          body={t('workspaceViews.saved.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              href={signInHref({
                pathname: `/workspace-views/${viewId}`,
                search: '',
              })}
            >
              {t('workspaceViews.saved.signInAction')}
            </Button>
          }
        />
      </SavedWorkspaceViewFrame>
    );
  }

  if (sessionPending) {
    return (
      <SavedWorkspaceViewFrame title={t('workspaceViews.saved.title')}>
        <div aria-busy="true" className="flex flex-col gap-4">
          <Skeleton shape="block" count={3} />
        </div>
      </SavedWorkspaceViewFrame>
    );
  }

  if (profile === null) {
    return (
      <SavedWorkspaceViewFrame title={t('workspaceViews.saved.title')}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('workspaceViews.saved.accountErrorTitle')}
          body={sessionError?.message ?? t('workspaceViews.saved.genericError')}
        />
      </SavedWorkspaceViewFrame>
    );
  }

  if (view.error !== null) {
    const notFound = view.error.code === 'not_found';
    return (
      <SavedWorkspaceViewFrame title={t('workspaceViews.saved.title')}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={
            /* ⚠ HYPHEN, matching the views route and FR-VIEW-031. */
            notFound
              ? t('workspaceViews.saved.notFound')
              : t('workspaceViews.saved.loadFailedTitle')
          }
          {...(notFound ? {} : { body: view.error.message })}
        />
      </SavedWorkspaceViewFrame>
    );
  }

  if (view.data === undefined) {
    return (
      <SavedWorkspaceViewFrame title={t('workspaceViews.saved.title')}>
        <div aria-busy="true" className="flex flex-col gap-4">
          <Skeleton shape="block" count={3} />
        </div>
      </SavedWorkspaceViewFrame>
    );
  }

  const savedView = view.data;
  const renderedSavedView = {
    ...savedView,
    selectedBoardIds: [...(localSelectedBoardIds ?? savedView.selectedBoardIds)],
    filters: localFilters ?? savedView.filters,
  };
  const isCreator = savedView.creator.id === profile.id;
  const workspaceName =
    workspace.data?.name ?? t('workspaceViews.saved.workspaceFallback');
  const managementError =
    renameView.error?.message ??
    setVisibility.error?.message ??
    setBoards.error?.message ??
    setFilters.error?.message ??
    deleteView.error?.message;
  const saving =
    renameView.isPending ||
    setVisibility.isPending ||
    setBoards.isPending ||
    setFilters.isPending ||
    deleteView.isPending;

  function replaceMonth(nextMonth: string): void {
    const params = new URLSearchParams();
    params.set('month', nextMonth);
    router.history.replace(`${location.pathname}?${params.toString()}`);
    router.history.flush();
  }

  return (
    <SavedWorkspaceViewPage
      view={renderedSavedView}
      currentUserId={profile.id}
      saving={saving}
      managementError={managementError}
      onRename={(title) => renameView.mutate({ viewId: savedView.id, title })}
      onVisibilityChange={(visibility) =>
        setVisibility.mutate({ viewId: savedView.id, visibility })
      }
      onDelete={() =>
        deleteView.mutate(
          { viewId: savedView.id, workspaceId: savedView.workspace.id },
          {
            onSuccess: () => {
              void navigate({ href: `/w/${savedView.workspace.id}/views` });
            },
          },
        )
      }
    >
      {savedView.kind === 'table' ? (
        <WorkspaceTablePage
          workspaceName={workspaceName}
          boards={boards.data ?? []}
          selectedBoardIds={renderedSavedView.selectedBoardIds}
          editableBoardIds={[]}
          filter={tableFilterFromWorkspaceView(renderedSavedView.filters)}
          members={members.data ?? []}
          boardMembers={[]}
          labels={labels}
          lists={tableLists}
          table={table.data}
          loadingBoards={boards.isPending}
          loadingFilters={loadingFilters}
          loadingTable={table.isPending}
          configurationLocked={!isCreator}
          error={table.error?.message}
          onBoardSelectionChange={(selectedBoardIds) => {
            if (!isCreator || selectedBoardIds.length === 0) return;
            setLocalSelectedBoardIds([...selectedBoardIds]);
            setBoards.mutate(
              { viewId: savedView.id, selectedBoardIds },
              {
                onError: () => setLocalSelectedBoardIds(savedView.selectedBoardIds),
              },
            );
          }}
          onFilterChange={(filter) => {
            if (!isCreator) return;
            const filters = workspaceViewFiltersFromTable(filter);
            setLocalFilters(filters);
            setFilters.mutate(
              {
                viewId: savedView.id,
                filters,
              },
              {
                onError: () => setLocalFilters(savedView.filters),
              },
            );
          }}
          onRetry={() => {
            void view.refetch();
            void workspace.refetch();
            void boards.refetch();
            void members.refetch();
            for (const query of labelQueries) void query.refetch();
            for (const query of listQueries) void query.refetch();
            void table.refetch();
          }}
        />
      ) : (
        <WorkspaceCalendarPage
          workspaceName={workspaceName}
          boards={boards.data ?? []}
          selectedBoardIds={renderedSavedView.selectedBoardIds}
          filter={calendarFilterFromWorkspaceView(renderedSavedView.filters)}
          members={members.data ?? []}
          labels={labels}
          lists={calendarLists}
          calendar={calendar.data}
          month={month}
          loadingBoards={boards.isPending}
          loadingFilters={loadingFilters}
          loadingCalendar={calendar.isPending}
          configurationLocked={!isCreator}
          error={calendar.error?.message}
          onBoardSelectionChange={(selectedBoardIds) => {
            if (!isCreator || selectedBoardIds.length === 0) return;
            setLocalSelectedBoardIds([...selectedBoardIds]);
            setBoards.mutate(
              { viewId: savedView.id, selectedBoardIds },
              {
                onError: () => setLocalSelectedBoardIds(savedView.selectedBoardIds),
              },
            );
          }}
          onFilterChange={(filter) => {
            if (!isCreator) return;
            const filters = workspaceViewFiltersFromCalendar(filter);
            setLocalFilters(filters);
            setFilters.mutate(
              {
                viewId: savedView.id,
                filters,
              },
              {
                onError: () => setLocalFilters(savedView.filters),
              },
            );
          }}
          onPreviousMonth={() => replaceMonth(previousCalendarMonth(month.month))}
          onNextMonth={() => replaceMonth(nextCalendarMonth(month.month))}
          onRetry={() => {
            void view.refetch();
            void workspace.refetch();
            void boards.refetch();
            void members.refetch();
            for (const query of labelQueries) void query.refetch();
            for (const query of listQueries) void query.refetch();
            void calendar.refetch();
          }}
        />
      )}
    </SavedWorkspaceViewPage>
  );
}

function SavedWorkspaceViewFrame({
  children,
  title,
}: {
  readonly children: ReactNode;
  readonly title?: string | undefined;
}) {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 sm:px-6">
      {title === undefined ? null : <h1 className="sr-only">{title}</h1>}
      {children}
    </div>
  );
}

function dedupeById<T extends { readonly id: string }>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  const deduped: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    deduped.push(item);
  }
  return deduped;
}
